import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// Read-only, unauthenticated schedule view. Reached via the tokenised link that
// dynamic-scheduling announcements share on WhatsApp, so it must render for
// someone who is not signed in and may be on a phone.

interface PublicGroup {
  round_number: number;
  group_number: number;
  slot_date: string | null;
  am_pm: 'AM' | 'PM' | null;
  course_name: string | null;
  suggested_course_name: string | null;
  is_leaderboard_round: boolean;
  tee_time: string | null;
  reservation_name: string | null;
  complex_name: string | null;
  players: string[];
}

interface PublicPlayerEvent {
  slot_date: string | null;
  am_pm: 'AM' | 'PM' | null;
  course_name: string | null;
  suggested_course_name: string | null;
  tee_time: string | null;
  reservation_name: string | null;
  complex_name: string | null;
  group_number: number;
  is_leaderboard_round: boolean;
  partners: string[];
}

interface PublicPlayer {
  name: string;
  rounds: number;
  events: PublicPlayerEvent[];
}

interface PublicDiagnostic {
  severity: 'error' | 'warning' | 'info';
  message: string;
}

interface PublicSchedulePayload {
  tournament_name: string;
  status: 'draft' | 'published';
  generated_at: string;
  diagnostics: PublicDiagnostic[];
  per_player: PublicPlayer[];
  groups: PublicGroup[];
}

const fmtDate = (iso: string | null) => {
  if (!iso) return 'Date TBD';
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
};
const fmtSession = (amPm: 'AM' | 'PM' | null) =>
  amPm === 'AM' ? 'Morning' : amPm === 'PM' ? 'Afternoon' : '';

// Mirrors the app's chip: amber lightbulb while a course is only suggested,
// green tick once a tee time confirms it.
const CourseChip: React.FC<{ name: string; confirmed?: boolean }> = ({ name, confirmed }) => (
  <div style={{ ...S.chip, ...(confirmed ? S.chipOk : S.chipSuggest) }}>
    <span aria-hidden="true">{confirmed ? '✓' : '💡'}</span>
    <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{name}</span>
    {!confirmed && <span style={S.chipNote}>suggested</span>}
  </div>
);

const PublicSchedule: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicSchedulePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'days' | 'players'>('days');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/public/schedule/${token}`);
        if (!res.ok) throw new Error(res.status === 404 ? 'This schedule link is not valid.' : 'Could not load the schedule.');
        setData(await res.json());
      } catch (e: any) {
        setError(e.message || 'Could not load the schedule.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) return <div style={S.wrap}><div style={S.muted}>Loading schedule…</div></div>;
  if (error) return <div style={S.wrap}><div style={S.error}>{error}</div></div>;
  if (!data) return null;

  // Group by day, then by session, preserving chronological order.
  const days: { date: string | null; sessions: { amPm: 'AM' | 'PM' | null; groups: PublicGroup[] }[] }[] = [];
  for (const g of data.groups) {
    let day = days.find((d) => d.date === g.slot_date);
    if (!day) { day = { date: g.slot_date, sessions: [] }; days.push(day); }
    let ses = day.sessions.find((x) => x.amPm === g.am_pm);
    if (!ses) { ses = { amPm: g.am_pm, groups: [] }; day.sessions.push(ses); }
    ses.groups.push(g);
  }
  days.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  for (const d of days) d.sessions.sort((a, b) => (a.amPm || '').localeCompare(b.amPm || ''));

  return (
    <div style={S.wrap}>
      <h1 style={S.h1}>{data.tournament_name}</h1>
      <div style={S.sub}>
        Tournament schedule
        {data.status === 'draft' && <span style={S.draft}>Draft</span>}
      </div>

      {(data.diagnostics || []).length > 0 && (
        <details style={S.diagBox} open={(data.diagnostics || []).some((d) => d.severity === 'error')}>
          <summary style={S.diagSummary}>
            Schedule notes ({data.diagnostics.length})
            {data.diagnostics.some((d) => d.severity === 'error') && (
              <span style={S.diagErrCount}>
                {data.diagnostics.filter((d) => d.severity === 'error').length} need attention
              </span>
            )}
          </summary>
          <ul style={S.diagList}>
            {data.diagnostics.map((d, i) => (
              <li key={i} style={{ ...S.diagItem, color: d.severity === 'error' ? '#842029'
                : d.severity === 'warning' ? '#664d03' : '#495057' }}>
                <span aria-hidden="true">
                  {d.severity === 'error' ? '✕' : d.severity === 'warning' ? '⚠' : 'ℹ'}
                </span>{' '}
                {d.message}
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Two ways in: by day, or "just show me my rounds". */}
      <div style={S.tabs}>
        <button
          type="button"
          onClick={() => setView('days')}
          style={{ ...S.tab, ...(view === 'days' ? S.tabActive : {}) }}
        >
          By day
        </button>
        <button
          type="button"
          onClick={() => setView('players')}
          style={{ ...S.tab, ...(view === 'players' ? S.tabActive : {}) }}
        >
          By player
        </button>
      </div>

      {days.length === 0 && <div style={S.muted}>No groups have been scheduled yet.</div>}

      {view === 'players' && (
        <div style={S.grid}>
          {(data.per_player || []).map((p) => (
            <section key={p.name} style={S.dayCard}>
              <header style={S.dayHeader}>
                {p.name}
                <span style={S.roundCount}>{p.rounds} round{p.rounds === 1 ? '' : 's'}</span>
              </header>
              <div style={{ padding: '10px 12px' }}>
                {p.events.length === 0 && <div style={S.muted}>Not scheduled yet.</div>}
                {p.events.map((ev, i) => (
                  <div key={i} style={S.playerEvent}>
                    <div style={S.playerEventHead}>
                      {fmtDate(ev.slot_date)}
                      <span style={{ ...S.badge, ...(ev.am_pm === 'AM' ? S.badgeAm : S.badgePm), marginBottom: 0 }}>
                        {fmtSession(ev.am_pm)}
                      </span>
                      {ev.is_leaderboard_round && <span>🏆</span>}
                    </div>
                    {ev.tee_time && <div style={S.tee}>⏰ {ev.tee_time}</div>}
                    {(ev.course_name || ev.suggested_course_name) && (
                      <CourseChip
                        name={(ev.course_name || ev.suggested_course_name) as string}
                        confirmed={!!ev.course_name}
                      />
                    )}
                    {ev.reservation_name && (
                      <div style={S.resv}>Reserved under {ev.reservation_name}</div>
                    )}
                    {ev.complex_name && <div style={S.resvFaint}>at {ev.complex_name}</div>}
                    <div style={S.partners}>
                      {ev.partners.length > 0 ? `with ${ev.partners.join(', ')}` : 'on their own'}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {view === 'days' && (
      <div style={S.grid}>
        {days.map((day) => (
          <section key={day.date || 'tbd'} style={S.dayCard}>
            <header style={S.dayHeader}>{fmtDate(day.date)}</header>
            {day.sessions.map((ses) => (
              <div key={ses.amPm || 'x'} style={S.session}>
                <div style={{ ...S.badge, ...(ses.amPm === 'AM' ? S.badgeAm : S.badgePm) }}>
                  {fmtSession(ses.amPm)}
                </div>
                {ses.groups.map((g) => (
                  <div key={`${g.round_number}-${g.group_number}`} style={S.group}>
                    <div style={S.groupTitle}>
                      Group {g.group_number}
                      {g.is_leaderboard_round && <span style={S.trophy}>🏆</span>}
                    </div>
                    {g.tee_time && <div style={S.tee}>⏰ {g.tee_time}</div>}
                    {(g.course_name || g.suggested_course_name) && (
                      <CourseChip
                        name={(g.course_name || g.suggested_course_name) as string}
                        confirmed={!!g.course_name}
                      />
                    )}
                    {g.reservation_name && (
                      <div style={S.resv}>Reserved under {g.reservation_name}</div>
                    )}
                    {g.complex_name && <div style={S.resvFaint}>at {g.complex_name}</div>}
                    {g.players.map((p) => <div key={p} style={S.player}>{p}</div>)}
                  </div>
                ))}
              </div>
            ))}
          </section>
        ))}
      </div>
      )}

      <footer style={S.footer}>
        Read-only view · generated {new Date(data.generated_at).toLocaleString()}
      </footer>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: {
    maxWidth: 1140, margin: '0 auto', padding: '18px 16px 56px', boxSizing: 'border-box',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    color: '#212529',
  },
  h1: { fontSize: 24, margin: '0 0 2px' },
  sub: { fontSize: 13, color: '#6c757d', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 },
  draft: { fontSize: 11, fontWeight: 700, background: '#fff3cd', color: '#856404', borderRadius: 4, padding: '2px 8px' },
  muted: { color: '#6c757d', fontSize: 14 },
  error: { color: '#dc3545', fontSize: 14 },
  // Wrapping grid so every day is reachable on any screen without side-scrolling.
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, alignItems: 'start' },
  dayCard: { border: '1px solid #dee2e6', borderRadius: 8, background: '#fff', overflow: 'hidden', minWidth: 0 },
  dayHeader: { padding: '8px 12px', background: '#f8f9fa', borderBottom: '1px solid #dee2e6', fontWeight: 700, fontSize: 13 },
  session: { padding: '10px 12px', borderTop: '1px solid #f1f3f5' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700, marginBottom: 8 },
  badgeAm: { background: '#e7f1ff', color: '#084298' },
  badgePm: { background: '#fff3cd', color: '#856404' },
  group: { border: '1px solid #dee2e6', borderRadius: 6, padding: '8px 10px', marginBottom: 8 },
  groupTitle: { fontWeight: 700, fontSize: 12, color: '#0d6efd', marginBottom: 4 },
  trophy: { marginLeft: 6 },
  tee: { fontSize: 11, fontWeight: 700, color: '#155724', background: '#d1e7dd', borderRadius: 4, padding: '2px 7px', display: 'inline-block', marginBottom: 4 },
  course: { fontSize: 11, color: '#495057', marginBottom: 4 },
  chip: {
    display: 'inline-flex', alignItems: 'flex-start', gap: 4, fontSize: 11,
    fontWeight: 600, lineHeight: 1.35, borderRadius: 4, padding: '2px 6px',
    marginBottom: 4, maxWidth: '100%',
  },
  chipSuggest: { color: '#7a5c00', background: '#fff8e1', border: '1px dashed #e0b400' },
  chipOk: { color: '#0a3622', background: '#d1e7dd', border: '1px solid #a3cfbb' },
  chipNote: { fontWeight: 400, opacity: 0.8 },
  player: { fontSize: 13, padding: '1px 0' },
  resv: { fontSize: 11, color: '#495057', marginBottom: 2, overflowWrap: 'anywhere' },
  resvFaint: { fontSize: 11, color: '#adb5bd', marginBottom: 2, overflowWrap: 'anywhere' },
  diagBox: {
    border: '1px solid #dee2e6', borderRadius: 8, background: '#fff',
    padding: '10px 12px', marginBottom: 14,
  },
  diagSummary: { fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  diagErrCount: { fontSize: 11, fontWeight: 700, background: '#f8d7da', color: '#842029', borderRadius: 4, padding: '2px 8px' },
  diagList: { margin: '8px 0 0', paddingLeft: 18 },
  diagItem: { fontSize: 12.5, lineHeight: 1.5, marginBottom: 4, overflowWrap: 'anywhere' },
  tabs: { display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' },
  tab: {
    border: '1px solid #dee2e6', background: '#fff', color: '#495057',
    borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer',
  },
  tabActive: { background: '#0d6efd', color: '#fff', borderColor: '#0d6efd', fontWeight: 600 },
  roundCount: { float: 'right', fontWeight: 400, fontSize: 11, color: '#6c757d' },
  playerEvent: { borderTop: '1px solid #f1f3f5', padding: '8px 0' },
  playerEventHead: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 13, fontWeight: 600, marginBottom: 4 },
  partners: { fontSize: 12, color: '#6c757d' },
  footer: { marginTop: 24, fontSize: 11, color: '#adb5bd' },
};

export default PublicSchedule;
